import moment from 'moment'

function dateTimestamp (value) {
  if (!(value instanceof Date)) {
    return null
  }
  const time = value.getTime()
  return Number.isNaN(time) ? null : time
}

/** Keep the same Date object when only the instance changed, not the time. */
export function keepDateIfUnchanged (current, next) {
  return dateTimestamp(current) === dateTimestamp(next) ? (current ?? null) : (next ?? null)
}

/**
 * Get all weekends (Saturdays and Sundays) in a given month of a specific year.
 *
 * @param {number} year - The year for which to get the weekends.
 * @param {number} month - The month (1-indexed) for which to get the weekends.
 * @returns {Date[]} An array of Date objects representing the weekends in the specified month.
 */
export function getWeekendsInMonth (year = moment.year(), month = moment().month() + 1) {
  const weekends = []
  const startDate = moment([year, month - 1]) // month is 0-indexed in moment
  const endDate = startDate.clone().endOf('month')

  for (let date = startDate; date.isBefore(endDate) || date.isSame(endDate, 'day'); date.add(1, 'day')) {
    if (date.day() === 6 || date.day() === 0) { // 6 is Saturday, 0 is Sunday
      weekends.push(date.clone())
    }
  }

  return weekends.map(date => date.toDate())
}

/**
 * Generates an array of all dates in a given month and year, excluding the dates provided in the input array.
 *
 * @param {Array<string>} dateArray - An array of date strings to be excluded, formatted as 'YYYY-MM-DD'.
 * @param {number} [selectedYear=moment().year()] - The year for which to generate the dates. Defaults to the current year.
 * @param {number} [selectedMonth=moment().month()] - The month for which to generate the dates (1-indexed). Defaults to the current month.
 * @returns {Array<Date>} An array of Date objects representing all dates in the specified month and year, excluding the dates in dateArray.
 */
export function getAllDatesExcluding (dateArray, selectedYear = moment().year(), selectedMonth = moment().month()) {
  const currentYear = selectedYear
  const currentMonth = selectedMonth - 1 // 0-indexed
  const startDate = moment([currentYear, currentMonth, 1])
  const endDate = startDate.clone().endOf('month')
  const allDates = []

  for (let date = startDate; date.isBefore(endDate) || date.isSame(endDate, 'day'); date.add(1, 'day')) {
    allDates.push(date.clone())
  }

  const excludedDates = dateArray.map(date => moment(date).format('YYYY-MM-DD'))
  const filteredDates = allDates.filter(date => !excludedDates.includes(date.format('YYYY-MM-DD'))).map(date => date.toDate())

  return filteredDates
}
